import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

type Variant = 'default' | 'ghost' | 'outlined';

// RESTYLE — Web dark-premium: `outlined` usa una hairline dorada (acento de
// énfasis) en vez de un borde grueso de tinta.
const variants: Record<Variant, string> = {
  default: 'bg-fc-paper border border-fc-line shadow-sm',
  ghost: 'bg-transparent',
  outlined: 'bg-fc-paper border border-fc-gold/70 shadow-sm',
};

export type CardProps = HTMLAttributes<HTMLDivElement> & {
  variant?: Variant;
  /** When false, removes the default `p-4 md:p-6` padding. */
  padded?: boolean;
  /**
   * Set true if the whole card is a clickable affordance — adds hover state
   * and removes the default focus ring on its children's hand-off.
   */
  interactive?: boolean;
};

/**
 * Generic surface component. Three visual variants for use across event
 * listings, cart summary, mis-boletas, and the post-pago QR card.
 *
 * Responsive: padding scales at `md` (768) breakpoint. All variants pass
 * AA contrast on the `fc-paper` background.
 *
 * @example
 *   <Card variant="outlined">…</Card>
 *   <Card variant="default" interactive role="button" tabIndex={0}>…</Card>
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', padded = true, interactive = false, className = '', children, ...rest },
  ref,
) {
  const cls = [
    'rounded-card',
    variants[variant],
    padded ? 'p-4 md:p-6' : '',
    interactive
      ? 'cursor-pointer transition-colors hover:border-fc-gold focus-visible:outline-2 focus-visible:outline-fc-gold'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div ref={ref} className={cls} {...rest}>
      {children}
    </div>
  );
});

/** Heading slot for a Card. Optional — pure convenience. */
export function CardTitle({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <h3 className={`font-display text-lg text-fc-ink ${className}`.trim()}>{children}</h3>;
}

/** Body slot for a Card. Optional — pure convenience. */
export function CardBody({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={`mt-2 text-fc-muted ${className}`.trim()}>{children}</div>;
}
